type Rgb = { r: number; g: number; b: number };

const DEFAULT_ORG_ACCENT = '#10B981';

const TRANSPARENT_VALUES = new Set(['transparent', 'rgba(0,0,0,0)', 'rgba(0, 0, 0, 0)']);

const clamp = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const rgbToHex = ({ r, g, b }: Rgb) =>
  `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b)
    .toString(16)
    .padStart(2, '0')}`;

const parseHex = (value: string): Rgb | null => {
  const hex = value.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
};

const parseRgb = (value: string): Rgb | null => {
  const match = value.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
};

const parseColor = (value?: string): Rgb | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized || TRANSPARENT_VALUES.has(normalized)) return null;
  if (normalized.startsWith('#')) return parseHex(normalized);
  if (normalized.startsWith('rgb')) return parseRgb(normalized);
  return null;
};

const isWhiteLikeRgb = (rgb: Rgb, threshold = 245) =>
  rgb.r >= threshold && rgb.g >= threshold && rgb.b >= threshold;

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => ({
  r: from.r + (to.r - from.r) * amount,
  g: from.g + (to.g - from.g) * amount,
  b: from.b + (to.b - from.b) * amount,
});

const getUsableColor = (value?: string): string | null => {
  if (!value) return null;
  const normalized = value.trim();
  if (!normalized) return null;
  if (TRANSPARENT_VALUES.has(normalized.toLowerCase())) return null;
  return parseColor(normalized) ? normalized : null;
};

export const resolveOrgAccentColor = (
  attrs: Record<string, unknown>,
  props: Record<string, unknown>,
  strokeColor?: string
): string => {
  return (
    getUsableColor(strokeColor)
    || getUsableColor(attrs.strokeColor as string)
    || getUsableColor(attrs.borderColor as string)
    || getUsableColor(props.borderColor as string)
    || getUsableColor(attrs.accentColor as string)
    || getUsableColor(props.accentColor as string)
    || DEFAULT_ORG_ACCENT
  );
};

export const resolveOrgBorderColor = (
  attrs: Record<string, unknown>,
  props: Record<string, unknown>,
  strokeColor?: string
): string | null => {
  return (
    getUsableColor(strokeColor)
    || getUsableColor(attrs.strokeColor as string)
    || getUsableColor(attrs.borderColor as string)
    || getUsableColor(props.borderColor as string)
    || null
  );
};

export const resolveOrgStrokeWidth = (
  attrs: Record<string, unknown>,
  props: Record<string, unknown>,
  strokeWidth?: number
): number | null => {
  const candidate =
    (typeof strokeWidth === 'number' ? strokeWidth : null)
    ?? (typeof attrs.strokeWidth === 'number' ? (attrs.strokeWidth as number) : null)
    ?? (typeof attrs.borderWidth === 'number' ? (attrs.borderWidth as number) : null)
    ?? (typeof props.strokeWidth === 'number' ? (props.strokeWidth as number) : null)
    ?? (typeof props.borderWidth === 'number' ? (props.borderWidth as number) : null);

  if (candidate == null || !Number.isFinite(candidate) || candidate <= 0) return null;
  return candidate;
};

export const resolveOrgDash = (
  attrs: Record<string, unknown>,
  props: Record<string, unknown>,
  dash?: number[],
  dashEnabled?: boolean
): number[] | undefined => {
  const candidate = Array.isArray(dash)
    ? dash
    : Array.isArray(attrs.dash)
      ? (attrs.dash as number[])
      : Array.isArray(props.dash)
        ? (props.dash as number[])
        : undefined;

  const enabledCandidate =
    (typeof dashEnabled === 'boolean' ? dashEnabled : null)
    ?? (typeof attrs.dashEnabled === 'boolean' ? (attrs.dashEnabled as boolean) : null)
    ?? (typeof props.dashEnabled === 'boolean' ? (props.dashEnabled as boolean) : null);

  if (!candidate || candidate.length === 0) return undefined;
  if (enabledCandidate === false) return undefined;
  return candidate;
};

export const isNearWhiteColor = (value?: string): boolean => {
  const rgb = parseColor(value);
  if (!rgb) return false;
  return isWhiteLikeRgb(rgb);
};

export const buildAccentGradientStops = (baseColor: string, variant: 'bar' | 'avatar' = 'bar') => {
  const baseRgb = parseColor(baseColor) ?? parseColor(DEFAULT_ORG_ACCENT) ?? { r: 52, g: 211, b: 153 };
  const lighter = mix(baseRgb, { r: 255, g: 255, b: 255 }, 0.25);
  const darker = mix(baseRgb, { r: 0, g: 0, b: 0 }, 0.15);
  if (variant === 'avatar') {
    return [0, rgbToHex(lighter), 1, rgbToHex(darker)];
  }
  return [0, rgbToHex(lighter), 0.5, rgbToHex(baseRgb), 1, rgbToHex(darker)];
};

export const buildCardTintGradientStops = (baseColor: string) => {
  const baseRgb = parseColor(baseColor) ?? parseColor(DEFAULT_ORG_ACCENT) ?? { r: 16, g: 185, b: 129 };
  // Keep org card fills very close to white with only a subtle hue shift.
  const top = mix(baseRgb, { r: 255, g: 255, b: 255 }, 0.975);
  const bottom = mix(baseRgb, { r: 255, g: 255, b: 255 }, 0.94);
  return [0, rgbToHex(top), 1, rgbToHex(bottom)];
};

export const buildDarkCardTintGradientStops = (baseColor: string) => {
  const baseRgb = parseColor(baseColor) ?? parseColor(DEFAULT_ORG_ACCENT) ?? { r: 16, g: 185, b: 129 };
  // Dark mode: keep depth while preserving more accent tint/liveliness.
  const top = mix(baseRgb, { r: 96, g: 118, b: 150 }, 0.86);
  const bottom = mix(baseRgb, { r: 76, g: 98, b: 130 }, 0.86);
  return [0, rgbToHex(top), 1, rgbToHex(bottom)];
};
